/**
 * FeedbackModal — wewnętrzne narzędzie dla testerów (Maciej + Marcin Horowski + super_admin).
 * Zapisuje feedback / pomysły / bugi. Super_admin widzi cudze + swoje wpisy
 * (żeby koordynować priorytety między testerami).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, Lightbulb, Bug, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FeedbackItem {
  id: string;
  userId: string;
  type: "feedback" | "feature_request" | "bug";
  content: string;
  pageContext: string | null;
  status: "open" | "in_progress" | "done" | "wontdo";
  createdAt: string;
  authorEmail?: string;
}

const TYPE_META: Record<
  FeedbackItem["type"],
  { label: string; icon: typeof MessageSquare; color: string }
> = {
  feedback: {
    label: "Feedback",
    icon: MessageSquare,
    color: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  },
  feature_request: {
    label: "Pomysł / feature",
    icon: Lightbulb,
    color: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  },
  bug: { label: "Bug", icon: Bug, color: "bg-red-500/20 text-red-300 border-red-500/40" },
};

const STATUS_LABELS: Record<FeedbackItem["status"], string> = {
  open: "open",
  in_progress: "in progress",
  done: "done",
  wontdo: "won't do",
};

export function FeedbackModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [type, setType] = useState<FeedbackItem["type"]>("feedback");
  const [content, setContent] = useState("");

  const { data, isLoading } = useQuery<{ items: FeedbackItem[] }>({
    queryKey: ["autopilot-feedback"],
    queryFn: () => apiRequest("GET", "/api/autopilot-feedback"),
    enabled: open,
  });

  const saveMut = useMutation({
    mutationFn: (input: { type: string; content: string; pageContext?: string }) =>
      apiRequest("POST", "/api/autopilot-feedback", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autopilot-feedback"] });
      toast({ title: "Zapisano feedback", description: "Dzięki! Marcin/Maciej zobaczą." });
      setContent("");
    },
    onError: (err) =>
      toast({
        title: "Nie udało się zapisać",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      }),
  });

  const handleSave = () => {
    if (content.trim().length < 2) return;
    saveMut.mutate({ type, content: content.trim(), pageContext: window.location.pathname });
  };

  const items = data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> Feedback / Pomysły — autopilot reklam AI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* New feedback form */}
          <div className="space-y-2 border rounded p-3 bg-white/[0.02]">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Nowy wpis</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(TYPE_META) as Array<FeedbackItem["type"]>).map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const selected = type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition ${selected ? meta.color : "bg-white/[0.03] border-white/10 text-white/60 hover:text-white"}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Opisz co Ci się podoba/nie podoba, co byś zmienił, co dorobić. Im konkretniej tym lepiej."
              maxLength={5000}
              rows={4}
              className="bg-[#030407] border-white/10 text-sm"
            />
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-muted-foreground">
                {content.length}/5000 · automatycznie zapisze obecną stronę (
                {window.location.pathname})
              </span>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saveMut.isPending || content.trim().length < 2}
                className="gap-2"
              >
                {saveMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Wyślij
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Lista wpisów ({items.length})
            </div>
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                Brak wpisów. Zacznij od pierwszego ☝️
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => {
                  const meta = TYPE_META[item.type] ?? TYPE_META.feedback;
                  const Icon = meta.icon;
                  return (
                    <div key={item.id} className="border rounded p-3 bg-white/[0.02] space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-xs ${meta.color} gap-1`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </Badge>
                          {item.status !== "open" && (
                            <Badge variant="secondary" className="text-[10px]">
                              {STATUS_LABELS[item.status]}
                            </Badge>
                          )}
                          {item.authorEmail && (
                            <span className="text-[10px] text-muted-foreground">
                              — {item.authorEmail}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {new Date(item.createdAt).toLocaleString("pl-PL", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{item.content}</p>
                      {item.pageContext && (
                        <div className="text-[10px] text-muted-foreground font-mono">
                          📍 {item.pageContext}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
